/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { REPORT_EVENTS as ReportEventNames } from '@adp-arch/report-events';
import { reporter } from '@adp-arch/logger';
import { type BotInfoForUpdate } from '@adp-arch/idl/playground_api';
import { BotPageFromEnum } from '@adp-arch/bot-typings/common';
import { type UpdateDraftBotResponse } from '@adp-arch/bot-api/developer_api';
import { PlaygroundApi } from '@adp-arch/bot-api';

import { useBotInfoStore } from '@/store/bot-info';

import { type BizKey } from '../types';
import { storage } from '../../utils/storage';
import { usePageRuntimeStore } from '../../store/page-runtime';
import { useCollaborationStore } from '../../store/collaboration';

export async function saveFetcher(
  saveRequest: () => Promise<UpdateDraftBotResponse>,
  scopeKey: BizKey,
  options: { manageState?: boolean } = {},
) {
  const {
    editable,
    isPreview,
    pageFrom,
    init,
    updateSavingScope,
    setPageRuntimeByImmer,
  } = usePageRuntimeStore.getState();

  const { setCollaborationByImmer } = useCollaborationStore.getState();
  const isReadonly = () =>
    !editable || isPreview || pageFrom === BotPageFromEnum.Explore;

  if (isReadonly() || !init) {
    return;
  }

  const manageState = options.manageState ?? true;
  const normalizedScopeKey = String(scopeKey ?? 'unknown');
  const currentScope =
    usePageRuntimeStore.getState().savingInfo.scopes?.[normalizedScopeKey];
  const revision = (currentScope?.revision ?? 0) + 1;

  try {
    if (manageState) {
      updateSavingScope({
        scopeKey: normalizedScopeKey,
        status: 'saving',
        revision,
      });
    }

    const res = await saveRequest();

    if (manageState) {
      updateSavingScope({
        scopeKey: normalizedScopeKey,
        status: 'clean',
        revision,
      });
    }

    if (res) {
      setPageRuntimeByImmer(state => {
        state.hasUnpublishChange = res.data.has_change ?? false;
      });
      setCollaborationByImmer(state => {
        state.sameWithOnline = res.data.same_with_online ?? false;
        if (state.branch && res.data.branch) {
          state.branch = res.data.branch;
        }
      });
    }
    reporter.successEvent({
      eventName: ReportEventNames.AutosaveSuccess,
      meta: { itemType: scopeKey },
    });
  } catch (e) {
    const error = e instanceof Error ? e : new Error(String(e));
    if (manageState) {
      updateSavingScope({
        scopeKey: normalizedScopeKey,
        status: 'failed',
        revision,
        error,
      });
    }
    reporter.errorEvent({
      eventName: ReportEventNames.AutosaveError,
      error,
      meta: { itemType: scopeKey },
    });
    throw error;
  }
}

/**
 * Update the structure of bot draft information
 * @Returns update bots using different request bodies based on tags
 */
export function updateBotRequest(structPayload: BotInfoForUpdate) {
  const { botId } = useBotInfoStore.getState();
  return PlaygroundApi.UpdateDraftBotInfoAgw({
    bot_info: {
      bot_id: botId,
      ...structPayload,
    },
    base_commit_version: storage.baseVersion,
  });
}
